// プロモーション設定（書き込み前テーブル）の表示メタ定義。
// payload の施策別フィールドラベルは W2DB定義（各対象テーブル）から厳選。
// 未定義キーは汎用表示（キーそのまま）にフォールバックする。

export interface FieldDef {
  key: string
  label: string
}

// 施策メタ（施策コード → 名称・アイコン・色）
export const PROMO_META: Record<string, { name: string; icon: string; color: string }> = {
  coupon: { name: 'クーポン', icon: '🎟️', color: '#D08A1A' },
  point: { name: 'ポイント', icon: '⭐', color: '#7A5BD6' },
  campaign: { name: 'キャンペーン', icon: '🎯', color: '#D08A1A' },
  crm: { name: 'CRM', icon: '✉️', color: '#0C6E6A' },
  ad: { name: '広告', icon: '📣', color: '#0C6E6A' },
  bundle: { name: '同梱／セット', icon: '📦', color: '#0C6E6A' },
  novelty: { name: 'ノベルティ', icon: '🎁', color: '#7A5BD6' },
  // 2026-10-06 変更（SMART_HUB_DEV-164）：名称を変更（変更前：定期会員／定期値引き）
  subscribe: { name: '定期購入会員割引', icon: '🔁', color: '#1A9E6B' },
  discount: { name: '定期購入商品割引', icon: '🏷️', color: '#D24B4B' },
  // 2026-10-07 追加（SMART_HUB_DEV-164）：通常のセール（商品セール設定）
  sale: { name: 'セール', icon: '🛍️', color: '#D24B4B' },
  rank: { name: '会員ランク', icon: '🏅', color: '#7A5BD6' },
  shipping: { name: '送料', icon: '🚚', color: '#0C6E6A' }
}

// ステータス（ライフサイクル）→ ラベル・配色
export const STATUS_META: Record<string, { label: string; bg: string; color: string }> = {
  draft: { label: '下書き', bg: '#EEF1F4', color: '#5A636E' },
  approved: { label: '承認済', bg: '#EEF2FF', color: '#4F46E5' },
  queued: { label: 'APIキュー投入', bg: '#EEF2FF', color: '#4F46E5' },
  awaiting_manual: { label: '手動登録待ち', bg: '#FBF1DE', color: '#D08A1A' },
  registered: { label: '登録済・同期待ち', bg: '#EEF2FF', color: '#4F46E5' },
  verified: { label: '実在確認済', bg: '#E5F4ED', color: '#1A9E6B' },
  failed: { label: '失敗', bg: '#FBE9E9', color: '#D24B4B' },
  canceled: { label: '取消', bg: '#EEF1F4', color: '#5A636E' }
}
// ステータス遷移（表示用）
export const STATUS_FLOW = [
  'draft',
  'approved',
  'queued',
  'registered',
  'verified'
]

export const OPERATION_LABEL: Record<string, string> = {
  create: '新規',
  update: '更新',
  stop: '停止'
}
export const MODE_LABEL: Record<string, string> = {
  api: 'API連携',
  screen: '管理画面誘導',
  manual: '手動'
}

// 登録機能（function_key）別のフィールドスキーマ（W2対象テーブルの主要項目）
export const FIELD_SCHEMA: Record<string, FieldDef[]> = {
  // w2_Coupon
  coupon_main: [
    { key: 'coupon_name', label: '管理用クーポン名' },
    { key: 'coupon_disp_name', label: '表示用クーポン名' },
    { key: 'coupon_code', label: 'クーポンコード' },
    { key: 'coupon_type', label: 'クーポン種別' },
    { key: 'discount_price', label: '商品割引額' },
    { key: 'discount_rate', label: '商品割引率(%)' },
    { key: 'coupon_count', label: '利用可能回数' },
    { key: 'publish_date_bgn', label: '発行期間(開始)' },
    { key: 'publish_date_end', label: '発行期間(終了)' },
    { key: 'expire_day', label: '有効期限(日)' },
    { key: 'expire_date_bgn', label: '有効期間(開始)' },
    { key: 'expire_date_end', label: '有効期間(終了)' },
    { key: 'product_kbn', label: '対象商品区分' }
  ],
  // w2_PointRule
  point_rule: [
    { key: 'point_rule_name', label: 'ポイントルール名' },
    { key: 'point_rule_kbn', label: 'ルール区分' },
    { key: 'inc_type', label: '加算方法' },
    { key: 'inc_num', label: '加算数' },
    { key: 'inc_rate', label: '加算率(倍)' },
    { key: 'exp_bgn', label: '有効期間(開始)' },
    { key: 'exp_end', label: '有効期間(終了)' },
    { key: 'priority', label: '優先順位' },
    { key: 'valid_flg', label: '有効フラグ' }
  ],
  // w2_MailDistSetting
  crm_maildist: [
    { key: 'maildist_name', label: 'メール配信設定名' },
    { key: 'status', label: 'ステータス' },
    { key: 'target_id', label: 'ターゲットID' },
    { key: 'target_extract_flg', label: 'ターゲット抽出フラグ' },
    { key: 'last_dist_date', label: '最終配信開始日時' }
  ],
  // w2_AdvCode
  ad_advcode: [
    { key: 'advertisement_code', label: '広告コード' },
    { key: 'media_name', label: '媒体名' },
    { key: 'advcode_media_type_id', label: '広告媒体区分' },
    { key: 'media_cost', label: '媒体費' },
    { key: 'publication_date_from', label: '掲載期間(From)' },
    { key: 'publication_date_to', label: '掲載期間(To)' },
    { key: 'valid_flg', label: '有効フラグ' }
  ],
  // w2_SetPromotion
  bundle_setpromotion: [
    { key: 'setpromotion_name', label: '管理用セットプロモ名' },
    { key: 'setpromotion_disp_name', label: '表示用セットプロモ名' },
    { key: 'promotion_type', label: 'プロモーション種別' },
    { key: 'product_discount_kbn', label: '商品割引区分' },
    { key: 'product_discount_setting', label: '商品割引設定' },
    { key: 'multiple_apply_flg', label: '重複適用フラグ' },
    { key: 'begin_date', label: '開始日時' },
    { key: 'end_date', label: '終了日時' },
    { key: 'target_member_rank', label: '適用会員ランク' }
  ],
  // w2_Novelty
  novelty_setting: [
    { key: 'novelty_name', label: 'ノベルティ名(管理用)' },
    { key: 'novelty_disp_name', label: 'ノベルティ名(表示用)' },
    { key: 'discription', label: '説明(管理用)' },
    { key: 'date_begin', label: '開始日時' },
    { key: 'date_end', label: '終了日時' },
    { key: 'valid_flg', label: '有効フラグ' }
  ],
  // w2_MemberRank（定期購入会員割引）
  // 2026-10-06 変更（SMART_HUB_DEV-164）：対象を顧客の定期契約（w2_FixedPurchase）から会員ランク設定へ。
  //   変更前の項目：fixed_purchase_kbn / fixed_purchase_status / order_kbn / order_payment_kbn /
  //   fixed_purchase_date_bgn / valid_flg
  subscribe_course: [
    { key: 'member_rank_id', label: '会員ランクID' },
    { key: 'member_rank_name', label: '会員ランク名' },
    { key: 'fixed_purchase_discount_rate', label: '定期会員割引率(%)' }
  ],
  // w2_ProductSale（セール。2026-10-07 変更：施策を discount から sale へ。キーは既存データのため据え置き）
  discount_productsale: [
    { key: 'productsale_name', label: '商品セール名' },
    { key: 'productsale_kbn', label: '商品セール区分' },
    { key: 'date_bgn', label: '開始日時' },
    { key: 'date_end', label: '終了日時' },
    { key: 'promotion_stackable_flag', label: 'プロモーション同時適用フラグ' },
    { key: 'valid_flg', label: '有効フラグ' }
  ],
  // w2_MemberRankRule
  rank_rule: [
    { key: 'member_rank_rule_name', label: 'ランク付与ルール名' },
    { key: 'status', label: 'ステータス' },
    { key: 'target_extract_total_price_from', label: '合計購入金額(From)' },
    { key: 'target_extract_total_price_to', label: '合計購入金額(To)' },
    { key: 'target_extract_total_count_from', label: '合計購入回数(From)' },
    { key: 'target_extract_total_count_to', label: '合計購入回数(To)' },
    { key: 'rank_change_type', label: 'ランク付与方法' },
    { key: 'rank_change_rank_id', label: '指定付与ランクID' },
    { key: 'exec_timing', label: '実行タイミング' }
  ]
  // coupon_schedule / point_schedule / campaign_feature / shipping_setting /
  // discount_saleprice は未定義 → 汎用表示（キーそのまま）
}
